import React from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Button
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RouterIcon from '@mui/icons-material/Router';
import NetworkCheckIcon from '@mui/icons-material/NetworkCheck';
import SpeedIcon from '@mui/icons-material/Speed';
import SettingsEthernetIcon from '@mui/icons-material/SettingsEthernet';

const services = [
  'Network infrastructure planning',
  'Router and switch installation',
  'Access point configuration',
  'Network cable installation',
  'Network security setup',
  'Network monitoring tools',
  'Performance optimization',
  'Bandwidth management',
  'VPN configuration',
  'Quality of Service (QoS) setup'
];

const benefits = [
  {
    icon: RouterIcon,
    title: 'Expert Installation',
    description: 'Certified network technicians with years of experience'
  },
  {
    icon: SpeedIcon,
    title: 'Fast Setup',
    description: 'Efficient installation with minimal downtime'
  },
  {
    icon: NetworkCheckIcon,
    title: 'Performance Guarantee',
    description: '100% satisfaction with network performance'
  },
  {
    icon: SettingsEthernetIcon,
    title: 'Quality Equipment',
    description: 'Enterprise-grade networking hardware'
  }
];

const supportedTypes = [
  'Small Office Networks',
  'Home Networks',
  'Enterprise Networks',
  'Wireless Networks',
  'Wired Networks',
  'Hybrid Networks',
  'Guest Networks',
  'IoT Networks',
  'Gaming Networks',
  'Multi-location Networks'
];

function NetworkInstallation() {
  return (
    <Box sx={{ py: 6, bgcolor: 'background.default' }}>
      {/* Hero Section */}
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="h2"
            component="h1"
            className="gradient-text"
            sx={{ mb: 3 }}
          >
            Professional Network Installation
          </Typography>
          <Typography
            variant="h5"
            sx={{ color: 'text.secondary', mb: 4 }}
          >
            Expert network setup and configuration services for homes and businesses
          </Typography>
          <Button
            component={RouterLink}
            to="/booking"
            variant="contained"
            size="large"
            sx={{
              py: 2,
              px: 4,
              fontSize: '1.2rem',
              background: 'linear-gradient(45deg, #2563eb 30%, #3b82f6 90%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #3b82f6 30%, #2563eb 90%)',
              }
            }}
          >
            Schedule Installation
          </Button>
        </Box>

        {/* Benefits Section */}
        <Grid container spacing={4} sx={{ mb: 8 }}>
          {benefits.map((benefit, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card className="glass-effect card-hover" sx={{ height: '100%' }}>
                <CardContent sx={{ textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Box sx={{ mb: 2, color: 'primary.main' }}>
                    {React.createElement(benefit.icon, { sx: { fontSize: 40 } })}
                  </Box>
                  <Typography variant="h6" gutterBottom>
                    {benefit.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {benefit.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Services List */}
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4, 
            mb: 8,
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)'
          }}
        >
          <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
            Our Network Services
          </Typography>
          <Grid container spacing={2}>
            {services.map((service, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={service} />
                  </ListItem>
                </List>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* Network Types Section */}
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4,
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)'
          }}
        >
          <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
            Network Types We Support
          </Typography>
          <Grid container spacing={2}>
            {supportedTypes.map((type, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                <Card 
                  className="glass-effect"
                  sx={{ 
                    textAlign: 'center',
                    py: 2
                  }}
                >
                  <Typography variant="h6">
                    {type}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Container>
    </Box>
  );
}

export default NetworkInstallation;
